// ===================================================================
// KAIXO - useModalClose
// ===================================================================
// Hook reusable para que cualquier modal se cierre con:
//   1. Tecla ESC (web)
//   2. Botón "atrás" físico de Android (en Capacitor)
//
// Sin este hook el usuario en Android se siente "atrapado" en un modal
// porque su gesto natural (botón atrás) cierra la app entera. Con esto
// el botón atrás cierra el modal y no sale de la app.
//
// Uso:
//   useModalClose(onClose, isOpen);
// ===================================================================

import { useEffect } from 'react';

let capacitorAppListenerStack = [];

export default function useModalClose(onClose, isOpen = true) {
    useEffect(() => {
        if (!isOpen || typeof onClose !== 'function') return;

        // 1) ESC en web
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKey);

        // 2) Android back en nativo (Capacitor App plugin)
        let removeBackListener = null;
        let cancelled = false;
        (async () => {
            try {
                // Solo intentamos cargar el plugin si estamos en Capacitor.
                // Si no, el dynamic import puede fallar — lo atrapamos.
                const { Capacitor } = await import('@capacitor/core');
                if (cancelled || !Capacitor?.isNativePlatform?.()) return;
                const { App } = await import('@capacitor/app');
                if (cancelled) return;
                // Registramos un listener que solo este modal consume.
                // Si hay varios modales apilados, el último abierto gana
                // (LIFO via capacitorAppListenerStack).
                const handler = ({ canGoBack: _ }) => {
                    // Solo si este es el listener "top of stack" — los demás
                    // (modales padre) se ignoran hasta que se cierre éste.
                    if (capacitorAppListenerStack[capacitorAppListenerStack.length - 1] === handler) {
                        onClose();
                    }
                };
                capacitorAppListenerStack.push(handler);
                const sub = await App.addListener('backButton', handler);
                removeBackListener = () => {
                    try { sub.remove(); } catch {}
                    capacitorAppListenerStack = capacitorAppListenerStack.filter(h => h !== handler);
                };
            } catch {
                // Capacitor no disponible (ej. testing en navegador puro). OK.
            }
        })();

        return () => {
            cancelled = true;
            window.removeEventListener('keydown', handleKey);
            if (removeBackListener) removeBackListener();
        };
    }, [onClose, isOpen]);
}
